'use client'

import { useMediaQuery, usePrevious } from '@mantine/hooks'
import { QrCode } from '@qr-front/icons/clone1/qr-code'
import { LineEye } from '@qr-front/icons/line-eye'
import { Switch } from '@qr-front/ui/components/Switch'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { useAtom, useAtomValue } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import { AnimatePresence, motion } from 'motion/react'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { SVGProps, useEffect } from 'react'

import { PhonePreview } from '@/components/PhonePreview'

import { captureBuilderEvent, STEP_TO_INDEX } from '../builderAnalytics'
import { QrPreviewScreen } from '../components/QrPreviewScreen'
import {
	isDemoModeAtom,
	previewModeAtom,
	qrContentAtom,
	qrTypeAtom,
	qrUriAtom,
} from '../generatorState'
import { useCurrGeneratorStep } from '../hooks'
import { getQrDataToEncode } from '../utils/buildQrLandingUrl'
import { qrPreviewDesignAtom } from './qrPreviewDesignState'
import { QR_TYPE_TO_CONTENT_SCREEN } from './utils/qrContentScreens'

const QrIcon = (props: SVGProps<SVGSVGElement>) => (
	<svg
		width={17}
		height={16}
		viewBox="0 0 17 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		{...props}
	>
		<path
			d="M14.12 5.47a.47.47 0 0 1-.468-.47V3.223a.375.375 0 0 0-.375-.375H11.5a.469.469 0 0 1 0-.937h1.778c.724 0 1.312.588 1.312 1.312V5c0 .259-.21.469-.469.469M2.88 5.47A.47.47 0 0 1 2.41 5V3.223c0-.724.59-1.312 1.312-1.312h1.779a.469.469 0 0 1 0 .937H3.722a.375.375 0 0 0-.374.375V5c0 .259-.21.469-.469.469M5.5 14.09H3.723a1.313 1.313 0 0 1-1.311-1.312v-1.779a.469.469 0 1 1 .937 0v1.778c0 .207.168.375.374.375h1.779a.469.469 0 0 1 0 .937m7.775.001H11.5a.469.469 0 0 1 0-.938h1.778a.375.375 0 0 0 .375-.374v-1.779a.469.469 0 1 1 .937 0v1.778c0 .724-.588 1.312-1.312 1.312M7.033 7.283h-1.78a.75.75 0 0 1-.75-.75v-1.78a.75.75 0 0 1 .75-.75h1.78a.75.75 0 0 1 .75.75v1.78a.75.75 0 0 1-.75.75M5.44 6.346h1.406V4.94H5.44zm1.593 5.651h-1.78a.75.75 0 0 1-.75-.75v-1.78a.75.75 0 0 1 .75-.75h1.78a.75.75 0 0 1 .75.75v1.78a.75.75 0 0 1-.75.75m-1.593-.938h1.406V9.655H5.44zm6.307.938h-1.78a.75.75 0 0 1-.75-.75v-1.78a.75.75 0 0 1 .75-.75h1.78a.75.75 0 0 1 .75.75v1.78a.75.75 0 0 1-.75.75m-1.593-.938h1.405V9.655h-1.405zm1.593-3.776h-1.78a.75.75 0 0 1-.75-.75v-1.78a.75.75 0 0 1 .75-.75h1.78a.75.75 0 0 1 .75.75v1.78a.75.75 0 0 1-.75.75m-1.593-.937h1.405V4.94h-1.405z"
			fill="currentColor"
		/>
	</svg>
)

export const GeneratorPreview = ({
	absolutelyPositionedSwitch,
	scaleDown,
}: {
	absolutelyPositionedSwitch: boolean
	scaleDown?: boolean
}) => {
	const t = useTranslations()

	const currStep = useCurrGeneratorStep()

	useHydrateAtoms([
		[previewModeAtom, currStep === 'design' ? 'qr' : 'preview'],
	])

	const [mode, setMode] = useAtom(previewModeAtom)
	const qrType = useAtomValue(qrTypeAtom)
	const qrUri = useAtomValue(qrUriAtom)
	const qrId = useSearchParams().get('id')
	const shouldDisableQrPreview = !qrId
	const qrPreviewDesign = useAtomValue(qrPreviewDesignAtom)
	const [isDemoMode, setIsDemoMode] = useAtom(isDemoModeAtom)
	const ContentScreen = qrType ? QR_TYPE_TO_CONTENT_SCREEN[qrType] : undefined
	const showSwitch = currStep !== 'type'
	const prevShowSwitch = usePrevious(showSwitch)
	const shouldAnimateSwitchEnter = showSwitch && prevShowSwitch === false
	const qrContent = useAtomValue(qrContentAtom)

	const isMobile = useMediaQuery('(max-width: 740px)')

	useEffect(() => {
		setMode(currStep === 'design' ? 'qr' : 'preview')
	}, [currStep, setMode])

	useEffect(() => {
		if (currStep === 'type') {
			setIsDemoMode(true)
		}
	}, [currStep, setIsDemoMode])

	const qrContentDemo = ContentScreen ? (
		<ContentScreen previewMode demoMode={isDemoMode} />
	) : undefined

	const qrPreview = (
		<QrPreviewScreen
			data={
				qrUri && qrContent
					? getQrDataToEncode({ qrUri, qrContent })
					: ''
			}
			qrDesign={qrPreviewDesign}
		/>
	)

	return (
		<div className="relative flex flex-col items-center">
			<AnimatePresence>
				{showSwitch ? (
					<motion.div
						key="preview-switch"
						initial={{
							opacity: showSwitch
								? shouldAnimateSwitchEnter
									? 0
									: 1
								: 0,
						}}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className={cn(
							absolutelyPositionedSwitch
								? 'absolute -top-4 left-1/2 -translate-x-1/2 -translate-y-full'
								: 'mb-4 w-fit',
						)}
					>
						<Switch
							visual="primary"
							value={mode}
							onValueChange={(value) => {
								captureBuilderEvent('builder_toggle_preview', {
									preview_mode: value,
									step_index: STEP_TO_INDEX[currStep],
									section: 'wizard_preview',
								})
								setMode(value as 'preview' | 'qr')
							}}
							itemList={[
								{
									value: 'preview',
									label: isMobile
										? ''
										: t(
												'generator__preview_switch__preview',
											),
									icon: <LineEye />,
								},
								{
									value: 'qr',
									label: isMobile
										? ''
										: t('generator__preview_switch__qr'),
									icon: getVersionForClone({
										base: <QrIcon />,
										clone1: <QrCode />,
									}),
									disabled: shouldDisableQrPreview,
								},
							]}
						/>
					</motion.div>
				) : null}
			</AnimatePresence>
			<div
				className={cn(
					scaleDown && 'flex h-[488px] scale-[0.87] items-center',
				)}
			>
				<PhonePreview
					content={mode === 'preview' ? qrContentDemo : qrPreview}
					contentKey={(mode === 'qr' && 'qr') || qrType || 'empty'}
					isQrPreview={mode === 'qr'}
				/>
			</div>
		</div>
	)
}
